import React, { useState, useEffect, useCallback } from 'react';
import { Users, Trophy, AlertCircle, PlayCircle, CheckCircle, Award, Calendar, Send } from 'lucide-react';
import { Wheel } from '../../../../types';
import { wheelsApi } from '../../../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../../../components/Modal';
import LoadingSpinner from '../../../../components/LoadingSpinner';

interface ExecuteDrawModalProps {
  isOpen: boolean;
  wheel: Wheel | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface Prize {
  id: number;
  name: string;
  image_url?: string;
}

interface Winner {
  spin_id: number;
  name: string;
  whatsapp: string;
  participated_at: string;
}

interface ExecuteDrawResult {
  success: boolean;
  total_participants: number;
  winner: Winner;
  prizes: Prize[];
  total_prizes: number;
  notification_sent: boolean;
  message: string;
}

const ExecuteDrawModal: React.FC<ExecuteDrawModalProps> = ({
  isOpen,
  wheel,
  onClose,
  onSuccess
}) => {
  const [participants, setParticipants] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [drawResult, setDrawResult] = useState<ExecuteDrawResult | null>(null);

  const loadParticipants = useCallback(async () => {
    if (!wheel) return;

    try {
      setIsLoading(true);
      const response = await wheelsApi.getParticipants(wheel.id);
      setParticipants(response.participants);
      setTotal(response.total);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao carregar participantes');
    } finally {
      setIsLoading(false);
    }
  }, [wheel]);

  useEffect(() => {
    if (isOpen && wheel) {
      loadParticipants();
    } else {
      // Reset state when modal closes
      setParticipants([]);
      setTotal(0);
      setDrawResult(null);
    }
  }, [isOpen, wheel, loadParticipants]);


  const handleExecuteDraw = async () => {
    if (!wheel) return;

    if (total === 0) {
      toast.error('Não há participantes para realizar o sorteio');
      return;
    }

    const confirmed = window.confirm(
      `Tem certeza que deseja realizar o sorteio?\n\nSerá sorteado 1 ganhador entre ${total} participante(s).\nO ganhador receberá TODOS os produtos da roda.\n\nEsta ação não pode ser desfeita.`
    );

    if (!confirmed) return;

    try {
      setIsExecuting(true);
      const result = await wheelsApi.executeScheduledDraw(wheel.id);

      setDrawResult(result);

      toast.success(`Sorteio realizado! ${result.winner.name} ganhou ${result.total_prizes} prêmio(s)!`);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao executar sorteio');
      setIsExecuting(false);
    }
  };

  const handleClose = () => {
    onClose();
    if (drawResult && drawResult.success) {
      onSuccess();
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      dateStyle: 'long',
      timeStyle: 'short'
    });
  };

  if (!wheel) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={drawResult ? 'Sorteio Realizado!' : `Executar Sorteio - ${wheel.name}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* Result Display */}
        {drawResult ? (
          <div className="text-center py-2">
            <div className="mb-6">
              <div className="mx-auto h-16 w-16 bg-musgo/15 border border-musgo/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-musgo" />
              </div>
              <h2 className="font-display text-2xl font-bold text-tinta mb-2">
                Sorteio realizado com <span className="italic text-bordo">sucesso!</span>
              </h2>
              <p className="text-sm text-tinta-soft italic">
                1 ganhador sorteado entre {drawResult.total_participants} participantes
              </p>
            </div>

            {/* Winner Info */}
            <div className="bg-musgo/10 border border-musgo/30 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Trophy className="h-7 w-7 text-mostarda-dark" />
                <h3 className="font-display text-xl font-bold text-tinta">Ganhador</h3>
              </div>

              <div className="bg-white border border-tinta/10 rounded-lg p-4 mb-4">
                <p className="font-display text-2xl font-bold text-bordo mb-1">{drawResult.winner.name}</p>
                <p className="text-sm text-tinta-soft">{drawResult.winner.whatsapp}</p>
              </div>

              {/* Prizes List */}
              <div className="bg-white border border-tinta/10 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Award className="h-5 w-5 text-mostarda-dark" />
                  <p className="font-bold text-tinta">Prêmios ({drawResult.total_prizes})</p>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {drawResult.prizes.map((prize, index) => (
                    <div key={prize.id} className="flex items-center gap-3 p-2 bg-creme-light border border-tinta/10 rounded-lg">
                      {prize.image_url && (
                        <img
                          src={prize.image_url}
                          alt={prize.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 text-left">
                        <p className="font-medium text-tinta text-sm">{prize.name}</p>
                      </div>
                      <span className="text-xs font-bold text-musgo">#{index + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Notification Status */}
            <div
              className={`flex items-start gap-3 rounded-lg p-4 mb-4 ${
                drawResult.notification_sent
                  ? 'bg-musgo/10 border border-musgo/30'
                  : 'bg-mostarda/15 border border-mostarda/40'
              }`}
            >
              {drawResult.notification_sent ? (
                <Send className="h-5 w-5 text-musgo flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-mostarda-dark flex-shrink-0 mt-0.5" />
              )}
              <p
                className={`text-sm text-left ${
                  drawResult.notification_sent ? 'text-musgo-dark' : 'text-mostarda-dark'
                }`}
              >
                {drawResult.notification_sent
                  ? 'Notificação enviada via WhatsApp com sucesso'
                  : 'Notificação WhatsApp não foi enviada'}
              </p>
            </div>

            <button
              onClick={handleClose}
              className="w-full px-4 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors"
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            {/* Draw Info */}
            {wheel.draw_date && (
              <div className="flex items-start gap-3 rounded-lg p-4 bg-tinta/5 border border-tinta/15">
                <Calendar className="h-5 w-5 text-tinta-soft flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-tinta mb-1">
                    Data Programada do Sorteio
                  </p>
                  <p className="font-display text-base font-bold text-tinta">
                    {formatDate(wheel.draw_date)}
                  </p>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="bg-mostarda/15 border border-mostarda/40 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-mostarda-dark flex-shrink-0 mt-0.5" />
                <div className="text-sm text-tinta">
                  <p className="font-bold mb-1">Atenção!</p>
                  <p className="text-tinta-soft leading-relaxed">
                    O sorteio será realizado apenas uma vez e não poderá ser desfeito.
                    Um produto será sorteado para cada participante baseado nos pesos configurados.
                  </p>
                </div>
              </div>
            </div>

            {/* Participants Count */}
            <div className="bg-white rounded-lg border border-tinta/15 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-bordo/8 border border-bordo/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-bordo" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft">
                      Total de Participantes
                    </p>
                    <p className="font-display text-2xl font-bold text-tinta">
                      {isLoading ? <LoadingSpinner size="sm" /> : total}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Participants List */}
            {!isLoading && participants.length > 0 && (
              <div className="bg-creme-light border border-tinta/10 rounded-lg p-4 max-h-64 overflow-y-auto">
                <h4 className="text-xs font-bold uppercase tracking-wider text-tinta mb-3">
                  Participantes
                </h4>
                <ul className="space-y-2">
                  {participants.slice(0, 10).map((participant, index) => (
                    <li key={participant.id} className="flex items-center gap-3 text-sm">
                      <span className="w-6 h-6 flex items-center justify-center bg-white border border-tinta/15 rounded-full text-xs font-bold text-tinta-soft">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-tinta">{participant.customer_name}</p>
                        <p className="text-tinta-soft text-xs">{participant.whatsapp}</p>
                      </div>
                    </li>
                  ))}
                  {participants.length > 10 && (
                    <li className="text-xs text-tinta-soft italic text-center pt-2">
                      + {participants.length - 10} participantes
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-tinta/10">
              <button
                onClick={onClose}
                disabled={isExecuting}
                className="flex-1 px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft bg-white hover:bg-creme-light hover:text-tinta transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleExecuteDraw}
                disabled={isExecuting || isLoading || total === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExecuting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Executando...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4" />
                    Executar Sorteio
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ExecuteDrawModal;
